import {
    Box, Typography, FormControl, InputLabel,
    Select, MenuItem, Chip, Checkbox, ListItemText,
    Button, SelectChangeEvent, Divider,
} from "@mui/material";
import { DATA_CATEGORIES } from "../DataCategories";

interface DataSelectorProps {
    selectedMetrics: { agristarMetrics: string[]; serviceMetrics: string[] };
    getMetricInfo: (value: string) => { label: string; color: string };
    setSelectedMetrics: (metrics: { agristarMetrics: string[]; serviceMetrics: string[] }) => void;
}

function DataSelector({ selectedMetrics, getMetricInfo, setSelectedMetrics }: DataSelectorProps) {
    // Combine all selected metrics for display
    const allSelectedMetrics = [...selectedMetrics.agristarMetrics, ...selectedMetrics.serviceMetrics];

    const handleMetricChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        const selectedValues = typeof value === 'string' ? value.split(',') : value;
        const agristarMetrics: string[] = [];
        const serviceMetrics: string[] = [];

        selectedValues.forEach(metric => {
            // Determine if metric belongs to agristar or service category
            if (isAgristarMetric(metric)) {
                agristarMetrics.push(metric);
            } else {
                serviceMetrics.push(metric);
            }
        });

        setSelectedMetrics({ agristarMetrics, serviceMetrics });
    };

    const isAgristarMetric = (metric: string): boolean => {
        // Find which category contains this metric
        const categoryEntry = Object.entries(DATA_CATEGORIES).find(
            ([categoryKey, category]) =>
                category.options.some(option => option.key === metric)
        );

        if (categoryEntry) {
            const [, category] = categoryEntry;
            return category.agristarMetric;
        }

        return false; // Default to service metrics if not found
    };
    const handleClearAll = () => {
        setSelectedMetrics({ agristarMetrics: [], serviceMetrics: [] });
    };

    // Filter out forbidden categories
    const forbiddenCategories = ["sucrose", "glucose"];
    const filteredDataCategories = Object.entries(DATA_CATEGORIES).filter(
        ([categoryKey]) => !forbiddenCategories.includes(categoryKey.toLowerCase())
    );

    return (<>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
                <InputLabel>Data Points</InputLabel>
                <Select
                    multiple
                    value={allSelectedMetrics}
                    onChange={handleMetricChange}
                    label="Data Points"
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value: any) => {
                                const metricInfo = getMetricInfo(value);
                                return (
                                    <Chip
                                        key={value}
                                        label={metricInfo.label}
                                        size="small"
                                        sx={{ backgroundColor: metricInfo.color + '20' }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                >
                    {filteredDataCategories.map(([categoryKey, category]) => [
                        <ListItemText
                            key={`header-${categoryKey}`}
                            primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: category.color }}>
                                    {category.label}
                                </Typography>
                            }
                            sx={{ pl: 1, backgroundColor: '#f5f5f5' }}
                        />,
                        ...category.options.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                                <Checkbox checked={allSelectedMetrics.indexOf(option.key) > -1} />
                                <ListItemText primary={option.label} />
                            </MenuItem>
                        ))
                    ])}
                </Select>
            </FormControl>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    Selected Metrics: {allSelectedMetrics.length}
                </Typography>

                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    AgriStar: {selectedMetrics.agristarMetrics.length} | Service: {selectedMetrics.serviceMetrics.length}
                </Typography>
            </Box>
            <Box>
                {allSelectedMetrics.length > 0 && (
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleClearAll}
                        sx={{ mt: 1 }}
                    >
                        Clear All
                    </Button>
                )}
            </Box>
        </Box>
    </>)
}

export default DataSelector;